import _ from 'lodash';
import { snapshotChartHelperService } from '@/services/snapshot-chart-helper-service';
import constants from '@/constants.js';

class ExcelReportService {
    buildWorkbookRequest (project, selectedDisplayPeriod) {
        let rawDataSheetMetrics = buildDataSheetMetrics(project.metrics,
            project.averageMetricsForEntireRange, selectedDisplayPeriod,
            constants.EXCEL_EXPORT_SHEET_TYPES.RAW_DATA_SHEET);
        let normedDataSheetMetrics = buildDataSheetMetrics(project.metrics,
            project.averageMetricsForEntireRange, selectedDisplayPeriod,
            constants.EXCEL_EXPORT_SHEET_TYPES.NORMALISED_DATA_SHEET);

        let normedWeightedDataSheetMetrics = buildDataSheetMetrics(project.metrics,
            project.averageMetricsForEntireRange, selectedDisplayPeriod,
            constants.EXCEL_EXPORT_SHEET_TYPES.NORMALISED_AND_WEIGHTED_DATA_SHEET);

        let finalScoreSheetMetrics = buildFinalScoreSheetMetrics(project.metrics,
            project.averageMetricsForEntireRange, selectedDisplayPeriod);

        let workbookRequest = {
            ResponseFileName: 'conversablity-project.xlsx',
            WorksheetRequests: [
                {
                    sheetType: constants.EXCEL_EXPORT_SHEET_TYPES.INFO_SHEET,
                    sheetTitle: 'Conversability - ' + project.name,
                    sheetName: 'INFO',
                    SelectedPeriod: selectedDisplayPeriod.value,
                    CreatedBy: project.createdBy,
                    ConversationTopic: project.conversationTopic,
                    Description: project.description,
                    Brands: finalScoreSheetMetrics.map(m => m.brandName),
                    BrandThemes: finalScoreSheetMetrics.map(m => m.brandTheme ? m.brandTheme : '')
                },
                {
                    sheetType: constants.EXCEL_EXPORT_SHEET_TYPES.RAW_DATA_SHEET,
                    sheetTitle: 'Raw Data - ' + selectedDisplayPeriod.value,
                    sheetName: 'RAW',
                    RowData: buildRowData(rawDataSheetMetrics, project.metricWeights)
                },
                {
                    sheetType: constants.EXCEL_EXPORT_SHEET_TYPES.NORMALISED_DATA_SHEET,
                    sheetTitle: 'Normalised Scores - ' + selectedDisplayPeriod.value,
                    sheetName: 'ANALYSE',
                    RowData: buildRowData(normedDataSheetMetrics, project.metricWeights)
                },
                {
                    sheetType: constants.EXCEL_EXPORT_SHEET_TYPES.NORMALISED_AND_WEIGHTED_DATA_SHEET,
                    sheetTitle: 'Normalised And Weighted Scores - ' + selectedDisplayPeriod.value,
                    sheetName: 'ADJUST',
                    RowData: buildRowData(normedWeightedDataSheetMetrics, project.metricWeights)
                },
                {
                    sheetType: constants.EXCEL_EXPORT_SHEET_TYPES.FINAL_SCORE_SHEET,
                    sheetTitle: 'Final Scores - ' + selectedDisplayPeriod.value,
                    sheetName: 'FINAL SCORES',
                    RowData: buildFinalScoreRowData(finalScoreSheetMetrics, project.pillarWeights)
                }
            ]
        };
        return workbookRequest;
    }
};

const buildFinalScoreSheetMetrics = (metrics, averageMetrics, selectedDisplayPeriod) => {
    let filteredMetrics;
    if (selectedDisplayPeriod.key === snapshotChartHelperService.AVG_METRICS_KEY) {
        filteredMetrics = averageMetrics;
    } else {
        filteredMetrics = metrics.filter(m => m.periodName === selectedDisplayPeriod.key);
    }
    return filteredMetrics;
};

const buildDataSheetMetrics = (metrics, averageMetrics, selectedDisplayPeriod, sheetType) => {
    let filteredMetrics;
    let dataSheetMetrics = [];
    if (selectedDisplayPeriod.key === snapshotChartHelperService.AVG_METRICS_KEY) {
        filteredMetrics = averageMetrics;
    } else {
        filteredMetrics = metrics.filter(m => m.periodName === selectedDisplayPeriod.key);
    }

    filteredMetrics.forEach(m => {
        let data;
        switch (sheetType) {
        case constants.EXCEL_EXPORT_SHEET_TYPES.RAW_DATA_SHEET:
            data = m.brandMetrics;
            break;
        case constants.EXCEL_EXPORT_SHEET_TYPES.NORMALISED_DATA_SHEET:
            data = m.normedBrandMetrics;
            break;
        case constants.EXCEL_EXPORT_SHEET_TYPES.NORMALISED_AND_WEIGHTED_DATA_SHEET:
            data = m.normedWeightedBrandMetrics;
            break;
        default:
            break;
        }
        dataSheetMetrics.push({
            name: m.brandName,
            ...data
        });
    });
    return dataSheetMetrics;
};

const buildFinalScoreRowData = (metrics, pillarWeights) => {
    let rowData = [];
    rowData.push({
        Axis: getAxisName('X'),
        AxisWeight: getPillarWeight(pillarWeights, constants.AXIS.X.sysName) / 100, // so the percentage cell format type can be applied in excel.
        BrandDataList: metrics.map(m => { return { BrandName: m.brandName, Value: m.xConversation }; })
    });
    rowData.push({
        Axis: getAxisName('Y'),
        AxisWeight: getPillarWeight(pillarWeights, constants.AXIS.Y.sysName) / 100, // so the percentage cell format type can be applied in excel.
        BrandDataList: metrics.map(m => { return { BrandName: m.brandName, Value: m.yQuality }; })
    });
    rowData.push({
        Axis: getAxisName('Z'),
        AxisWeight: getPillarWeight(pillarWeights, constants.AXIS.Z.sysName) / 100, // so the percentage cell format type can be applied in excel.
        BrandDataList: metrics.map(m => { return { BrandName: m.brandName, Value: m.zCommunity }; })
    });
    rowData.push({
        Axis: getAxisName('FS'),
        AxisWeight: 1,
        BrandDataList: metrics.map(m => { return { BrandName: m.brandName, Value: m.finalScore }; })
    });
    return rowData;
};

const buildRowData = (metrics, metricWeights) => {
    let rowData = [];
    if (!metricWeights) return rowData;
    metricWeights.forEach(mw => {
        rowData.push(
            {
                Axis: getAxisName(mw.axis),
                Tool: mw.tool,
                AxisWeight: 1,
                MetricName: mw.metric,
                MetricWeight: mw.weight / 100, // so the percentage cell format type can be applied in excel.
                Platform: mw.platform,
                BrandDataList: buildBrandData(metrics, mw.sysMetric)
            }
        );
    });
    return rowData;
};

const buildBrandData = (metrics, metricName) => {
    let brandDataList = [];
    metrics.forEach(m => {
        brandDataList.push(
            {
                BrandName: m.name,
                Value: m[metricName]
            }
        );
    });
    return brandDataList;
};

const getAxisName = (axis) => {
    switch (axis) {
    case 'X':
        return 'X-CONVERSATION';
    case 'Y':
        return 'Y-QUALITY';
    case 'Z':
        return 'Z-COMMUNITY';
    case 'FS':
        return 'FINAL SCORE';
    default:
        return '';
    }
};

const getPillarWeight = (pillarWeights, sysName) => {
    let weightObj = _.find(pillarWeights, w => w.sysMetric === sysName);
    return (weightObj && weightObj.weight) ? weightObj.weight : 0;
};

export const excelReportService = new ExcelReportService();
